import { Fraction } from '@emberswap/emberswap-sdk2/dist/entities'
import { UseQueryOptions } from '@tanstack/react-query'
import { useMemo } from 'react'
import { getAddress, isAddress, parseUnits } from 'viem'
import { useQuery } from 'wagmi'

import { SUPPORTED_CHAINS } from '../constants/graphql'

export const usePrices = ({
  chainId,
  options,
}: {
  chainId?: number
  options?: Omit<
    UseQueryOptions<Record<string, number>, unknown, Record<string, number>, string[]>,
    'queryKey' | 'queryFn' | 'initialData'
  >
}) => {
  const queryKey = useMemo(() => [`/api/prices/${chainId}`], [chainId])
  const { data, isError, isLoading } = useQuery(
    queryKey,
    () => fetch(`/api/prices/${chainId}`).then((response) => response.json()),
    { staleTime: 300, enabled: Boolean(chainId), ...options }
  )

  return useMemo(() => {
    if (!SUPPORTED_CHAINS.includes(Number(chainId)))
      return {
        isError: true,
        isLoading: false,
        data: {},
      }
    return {
      isError,
      isLoading,
      data:
        data && !isError && !isLoading && SUPPORTED_CHAINS.includes(Number(chainId))
          ? Object.entries(data.prices).reduce<Record<string, Fraction>>(
              (acc, [address, price]) => {
                if (isAddress(address)) {
                  acc[getAddress(address)] = new Fraction(
                    parseUnits(price.toFixed(18), 18).toString(),
                    parseUnits('1', 18).toString()
                  )
                }

                return acc
              },
              {}
            )
          : undefined,
    }
  }, [chainId, data, isError, isLoading])
}
